import { getPoiSceneDefByLabel, getSceneKeyByLabel } from '../../../../config/poiSceneConfig';
import { CommercialPanel } from "./panels/CommercialPanel";
import { CommunityPanel } from "./panels/CommunityPanel";
import { IndustrialPanel } from "./panels/IndustrialPanel";

interface SceneDetailItem {
  name: string;
  extractType: string;
  gid: string | number;
  objectid: string | number;
  geom: string;
}

interface SceneDetailPanelProps {
  scene: SceneDetailItem | null;
  visible: boolean;
  onClose: () => void;
  layerControlCollapsed: boolean;
  collapsed: boolean;
  onCollapsedChange: (collapsed: boolean) => void;
}

const SceneDetailPanel: React.FC<SceneDetailPanelProps> = ({
  scene,
  visible,
  onClose,
  layerControlCollapsed,
  collapsed,
  onCollapsedChange,
}) => {
  // 场景类型判断
  const sceneTypeKey = scene?.extractType
    ? (() => { try { return getSceneKeyByLabel(scene.extractType); } catch { return ''; } })()
    : '';
  const isCommercial = sceneTypeKey === 'commercial';
  const isResidential = sceneTypeKey === 'residential';
  const isIndustrial = sceneTypeKey === 'industrial';

  if (!visible || !scene) return null;

  const panelLeft = layerControlCollapsed ? 62 : 190;

  const def = scene.extractType
    ? (() => { try { return getPoiSceneDefByLabel(scene.extractType); } catch { return null; } })()
    : null;
  const dotColor = def?.color || '#6b7280';

  // 场景类型对应的图标和颜色
  const sceneIcon = isResidential
    ? "ri-home-4-line text-emerald-500"
    : isIndustrial
      ? "ri-building-4-line text-orange-500"
      : "ri-building-4-line text-blue-500";

  if (collapsed) {
    return (
      <div
        className="absolute top-36 z-[40]"
        style={{ width: 36, left: panelLeft }}
      >
        <div
          className="bg-white border border-slate-200 rounded-xl shadow-lg overflow-hidden cursor-pointer hover:bg-slate-50 transition-colors"
          onClick={() => onCollapsedChange(false)}
        >
          <div className="flex flex-col items-center py-3 gap-2">
            <i className={`${sceneIcon} text-sm`} />
            <span className="text-[10px] text-slate-500 font-medium whitespace-nowrap [writing-mode:vertical-rl]">
              {scene.name}
            </span>
            <i className="ri-arrow-right-s-line text-slate-400 text-sm" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="absolute top-36 z-[40]"
      style={{ width: 380, left: panelLeft }}
    >
      <div className="bg-white border border-slate-200 rounded-xl shadow-lg overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 bg-gradient-to-r from-slate-50 to-white">
          <div className="flex items-center gap-2">
            <i className={`${sceneIcon} text-sm`} />
            <h3 className="text-sm font-semibold text-slate-700">{scene.name}</h3>
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={() => onCollapsedChange(true)}
              className="w-5 h-5 flex items-center justify-center rounded hover:bg-slate-200 text-slate-400 hover:text-slate-600 cursor-pointer"
              title="折叠"
            >
              <i className="ri-subtract-line text-xs" />
            </button>
            <button
              onClick={onClose}
              className="w-5 h-5 flex items-center justify-center rounded hover:bg-slate-200 text-slate-400 hover:text-slate-600 cursor-pointer"
            >
              <i className="ri-close-line text-sm" />
            </button>
          </div>
        </div>

        {/* Scene ID */}
        <div className="px-4 pt-2.5 pb-1.5 border-b border-slate-100 bg-slate-50/50">
          <div className="flex items-center gap-1.5">
            <i className="ri-key-line text-slate-400 text-[11px]" />
            <span className="text-[11px] text-slate-500">场景ID：</span>
            <span className="text-[11px] font-medium text-slate-700">{scene.objectid || scene.gid}</span>
            <span className="mx-1.5 text-slate-300">|</span>
            <span
              className="w-1.5 h-1.5 rounded-full flex-shrink-0"
              style={{ backgroundColor: dotColor }}
            />
            <span className="text-[11px] text-slate-500">{scene.extractType || '未分类'}</span>
          </div>
        </div>

        {/* Address */}
        <div className="px-4 py-2 border-b border-slate-100 bg-slate-50/30">
          <div className="flex items-center gap-1.5">
            <i className="ri-map-pin-2-line text-slate-400 text-[11px]" />
            <span className="text-[11px] text-slate-600">江苏南京秦淮区中山南路49号</span>
          </div>
        </div>

        {/* Scene-type-specific panel content */}
        {isResidential && <CommunityPanel scene={scene} />}
        {isIndustrial && <IndustrialPanel scene={scene} />}
        {!isResidential && !isIndustrial && <CommercialPanel scene={scene} />}
      </div>
    </div>
  );
};

export default SceneDetailPanel;
